'use client'

import { Flex } from '@mantine/core'
import { useEffect, useMemo } from 'react'

import RichText, { createCoinSymbolConverters } from '@/app/[locale]/price/_components/Richtext'
import { SEGMENT_EVENTS } from '@/configs/dataTrackingEvent'
import useHasAppearedInViewport from '@/hooks/useHasAppearedInViewport'
import { useTradeDrawer } from '@/services/coin/cmsCampaign'
import { trackSegmentEvent } from '@/services/dataTracking'
import { isCoinVisible, isVisibleForCountry } from '@/utils/campaign'

import { useCoinContext } from '../../_contexts/CoinContext'
import styles from './index.module.css'

interface TradeDrawerProps {
  locale: string
  countryCode: string
  regionCode: string
}

export default function TradeDrawer({ locale, countryCode, regionCode }: TradeDrawerProps) {
  const { data } = useTradeDrawer(locale)
  const { coin } = useCoinContext()
  const { ref, hasAppearedInViewport } = useHasAppearedInViewport()

  const customConverters = useMemo(() => createCoinSymbolConverters(coin.symbol), [coin.symbol])

  useEffect(() => {
    if (
      hasAppearedInViewport &&
      data?.id &&
      data?.tradeDrawer &&
      isVisibleForCountry(data.allowedIps, data.restrictedIps, countryCode, regionCode)
    ) {
      trackSegmentEvent(SEGMENT_EVENTS.WebPriceCampaignMessageViewed, {
        campaign_id: data.id,
        coin: coin.symbol,
        page_path: window.location.pathname,
        locale,
      })
    }
  }, [hasAppearedInViewport])

  if (!data?.tradeDrawer?.campaignMessage) return null
  if (!isCoinVisible(data, coin.symbol)) return null
  if (!isVisibleForCountry(data.allowedIps, data.restrictedIps, countryCode, regionCode))
    return null

  const { campaignMessage, icon, defaultIcon } = data.tradeDrawer
  const displayIcon = icon ?? defaultIcon

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const anchor = (e.target as HTMLElement).closest('a')
    if (anchor) {
      trackSegmentEvent(SEGMENT_EVENTS.WebPriceCampaignMessageClicked, {
        campaign_id: data.id,
        coin: coin.symbol,
        page_path: window.location.pathname,
        locale,
        destination_url: anchor.href,
      })
    }
  }

  return (
    <Flex ref={ref} className={styles.tradeDrawer} gap={8} align="center">
      <img
        src={displayIcon.url}
        alt={displayIcon.alt}
        width={20}
        height={20}
        style={{ flexShrink: 0 }}
      />
      <RichText
        data={campaignMessage}
        className={styles.tradeDrawerText}
        customConverters={customConverters}
        onClick={handleClick}
      />
    </Flex>
  )
}
